import { Flex, Menu, MenuProps } from "antd";
import Sider from "antd/es/layout/Sider";
import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import DownArrow from "../../../assets/icons/sidebar/DownArrow";
import UpArrow from "../../../assets/icons/sidebar/UpArrow";
import logo_large from "../../../assets/images/logo-large.svg";
import logo_small from "../../../assets/images/logo-small.svg";
import AllSidebarMenu from "../../../constants/sidebar.constants";
import { stringToArray } from "../../../utils/array";
import Image from "../../ui/Image";

type MenuItem = Required<MenuProps>["items"][number];

interface SidebarItemProps {
  collapsed: boolean;
  openMenuKeys: string[];
  handleOpenMenu: (keys: string[]) => void;
  isMobile?: boolean;
  data?: any; // TODO:
}

export default function SidebarItems({
  collapsed,
  openMenuKeys,
  handleOpenMenu,
  isMobile,
  data,
}: SidebarItemProps) {
  const location = useLocation();
  const { authInfo } = useAppSelector((state) => state.auth);

  // const isEmailVerified = data?.data?.employee?.member?.profile?.email_verified;
  const isEmailVerified = true; // TODO:

  function getItem(
    label: ReactNode,
    path: string,
    icon?: ReactNode,
    badge?: string,
    children?: MenuItem[],
    roles?: string[],
  ): MenuItem {
    return {
      key: pathString(path),
      icon,
      children,
      label: children?.length ? (
        label
      ) : (
        <Link to={path}>
          {label}
          {badge && !collapsed ? (
            <button className="text-secondary text-xs font-medium uppercase px-2 py-0.5 bg-white rounded border border-secondary h-6 justify-center items-center inline-flex ml-1.5">
              beta
            </button>
          ) : undefined}
        </Link>
      ),
      roles,
    } as MenuItem;
  }

  // filter allowed menu
  const MenuItems: MenuItem[] = AllSidebarMenu.filter((el) =>
    el?.role?.find((rl) => rl === authInfo?.role),
  ).map(({ name, path, icon: Icon, children, badge }) =>
    getItem(
      name,
      path,
      Icon ? <Icon collapsed={collapsed} /> : undefined,
      badge,
      children?.length
        ? children
            .filter((el) => el?.role?.find((rl) => rl === authInfo?.role))
            .map(
              ({ name: cdName, path: cdPath, icon: CDIcon, badge: cdBadge }) =>
                getItem(
                  cdName,
                  cdPath,
                  CDIcon ? <CDIcon /> : undefined,
                  cdBadge,
                ),
            )
        : undefined,
    ),
  );

  // find active sub menu
  function getActiveMenu(data: any[]) {
    const strArr = stringToArray(location.pathname, "/");
    return data?.find((el) => el?.key === strArr[1])?.children ? true : false;
  }

  return (
    <div
      className={`sticky !bg-white z-50 ${
        !isEmailVerified && !isMobile
          ? "top-12 h-[calc(100vh-3rem)]"
          : "top-0 h-screen"
      }`}
    >
      <Sider
        theme="light"
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={312}
      >
        <div className={` bg-white`}>
          <Link to="/">
            <Flex align="center" justify="center" className="mt-4 mx-2">
              <Image
                preview={false}
                height={60}
                className="object-contain"
                alt="Vendor Management System"
                src={collapsed ? logo_small : logo_large}
              />
            </Flex>
          </Link>
          {/* Menu Divider */}
          <Flex
            className="px-5 my-2.5"
            align="center"
            justify={collapsed ? "center" : "start"}
            gap={8}
          >
            <h2 className="text-theme-slate-300 text-xs font-semibold">MENU</h2>
            <div className="w-full h-[1px] bg-theme-slate-200" />
          </Flex>

          {/* sidebar item section */}
          <div
            className={`!overflow-auto bg-white sticky custom-scrollbar ${
              !isEmailVerified && !isMobile
                ? "h-[calc(100vh-9.3rem)]"
                : "h-[calc(100vh-6.4rem)]"
            }`}
          >
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[
                activePathString(
                  location.pathname,
                  "/",
                  getActiveMenu(MenuItems),
                ),
              ]}
              openKeys={openMenuKeys}
              onOpenChange={handleOpenMenu}
              items={MenuItems}
              className={`!bg-white !px-4 !select-none ${
                collapsed ? "px-3" : ""
              }`}
              rootClassName="custom-sidebar"
              expandIcon={({ isSubMenu, isOpen }) => {
                if (isSubMenu && !collapsed) {
                  return isOpen ? <UpArrow /> : <DownArrow />;
                }
              }}
            />
          </div>
        </div>
      </Sider>
    </div>
  );
}

const activePathString = (
  path: string,
  splitText: string,
  isSubMenu?: boolean,
) => {
  const pathArray = stringToArray(path, splitText);
  return pathArray.slice(1, (isSubMenu ? 3 : 2) - 0).join("/");
};

const pathString = (path: string, splitText: string = "/") => {
  return stringToArray(path, splitText).slice(1, 3).join("/");
};
